import { useState } from 'react';
import { useForm } from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';
import { answerEmailIntakeFormQuestion } from '@api';
import { Field, Input, Label, ErrorMessage } from '@quickmd-llc/design-system';
import { EmailQuestion } from '@qmd-types/intakeForms';
import { SubmitButton } from './SubmitButton';
import { useIntakeForm } from '../../context/useIntakeForm';

type Props = {
  question: EmailQuestion;
};

type FormValues = {
  email: string;
};

export function EmailForm({ question }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { handleNextStep, intakeForm, updateAnswer } = useIntakeForm();
  const { handleSubmit, formState, register } = useForm<FormValues>({
    defaultValues: {
      email: question.email || undefined,
    },
  });
  const { errors } = formState;
  const hasError = Boolean(errors.email);

  const emailOptions = {
    required: {
      value: question.isRequired,
      message: 'This field is required',
    },
    validate: (value: string) => {
      if (!question.isRequired) {
        return;
      }

      const isInvalid = !isEmail(value);

      if (isInvalid) {
        return 'The email you provided is not valid';
      }
    },
  };

  async function onSubmit(data: FormValues) {
    const { intakeFormQuestionId } = question;
    const { intakeFormId } = intakeForm;

    setIsLoading(true);

    try {
      await answerEmailIntakeFormQuestion({
        intakeFormQuestionId,
        intakeFormId,
        data,
      });

      const updatedQuestion = {
        ...question,
        email: data.email,
      };

      updateAnswer(intakeFormQuestionId, updatedQuestion);
      handleNextStep();
    } catch {
      // @TODO: Handle error
      setIsLoading(false);
    }
  }

  return (
    <form className="flex flex-1 flex-col" onSubmit={handleSubmit(onSubmit)}>
      <Field error={hasError}>
        <Label>{question.question}</Label>

        {hasError && <ErrorMessage>{errors.email?.message}</ErrorMessage>}

        <Input
          {...register('email', emailOptions)}
          autoFocus
          placeholder="email@domain.com"
          type="email"
        />
      </Field>

      <SubmitButton isLoading={isLoading} />
    </form>
  );
}
